import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import Layout from '../components/layout/Layout';
import WheelDisplay from '../components/wheels/WheelDisplay';
import WheelEntryEditor from '../components/wheels/WheelEntryEditor';
import { getWheel, updateWheel } from '../services/firebaseWheelService';
import { getStoredWheelById, updateStoredWheel } from '../services/localStorage';
import { Wheel, WheelEntry } from '../types/wheel';
import { trackWheelEdit } from '../utils/trackingUtils';

/**
 * Page for editing an existing wheel
 */
const EditWheel: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  // Get storage type from location state or default to 'local'
  const storageType = location.state?.storageType || 'local';

  // State for wheel data
  const [wheelName, setWheelName] = useState<string>('');
  const [entries, setEntries] = useState<WheelEntry[]>([]);

  // UI state
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Load wheel data on mount
  useEffect(() => {
    const loadWheel = async () => {
      if (!id) {
        setError('No wheel ID provided');
        setIsLoading(false);
        return;
      }

      try {
        let wheel: Wheel | null = null;

        if (storageType === 'local') {
          // Load from localStorage
          wheel = getStoredWheelById(id);
        } else {
          // Load from Firebase
          wheel = await getWheel(id);
        }

        if (!wheel) {
          setError('Wheel not found');
          setIsLoading(false);
          return;
        }

        // Populate form with wheel data
        setWheelName(wheel.name);
        setEntries(wheel.entries);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading wheel:', error);
        setError('Failed to load wheel. Please try again.');
        setIsLoading(false);
      }
    };

    loadWheel();
  }, [id, storageType]);

  /**
   * Validate wheel data before saving
   */
  const validateWheel = (): boolean => {
    if (!wheelName.trim()) {
      setError('Please enter a name for your wheel');
      return false;
    }

    if (entries.length < 2) {
      setError('Please add at least 2 entries to your wheel');
      return false;
    }

    setError(null);
    return true;
  };

  /**
   * Handle saving the updated wheel to the appropriate storage
   */
  const handleSaveWheel = async () => {
    if (!id || !validateWheel()) return;

    try {
      setIsSaving(true);

      if (storageType === 'local') {
        // Update in localStorage
        const existingWheel = getStoredWheelById(id);
        if (!existingWheel) {
          setError('Wheel not found');
          return;
        }

        // Update wheel data
        const updatedWheel = updateStoredWheel({
          ...existingWheel,
          name: wheelName.trim(),
          entries: entries,
        });

        // Track wheel edit event
        trackWheelEdit(updatedWheel.id, wheelName.trim());

        // Navigate to the wheel detail page
        navigate(`/wheels/${updatedWheel.id}`);
      } else {
        // Update in Firebase
        // First get the current wheel to ensure we have all required fields
        const currentWheel = await getWheel(id);
        if (!currentWheel) {
          setError('Wheel not found');
          return;
        }

        // Only update what has changed, preserving all other fields
        await updateWheel(id, {
          ...currentWheel,
          name: wheelName.trim(),
          entries: entries.map(entry => ({
            ...entry,
            // Ensure each entry has an ID
            id: entry.id || `entry-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          })),
          // Don't update these fields
          id: currentWheel.id,
          createdAt: currentWheel.createdAt,
          createdBy: currentWheel.createdBy,
          spinCount: currentWheel.spinCount,
          favorites: currentWheel.favorites,
        });

        // Track wheel edit event
        trackWheelEdit(id, wheelName.trim());

        // Navigate to the wheel detail page
        navigate(`/wheels/${id}`);
      }
    } catch (err) {
      console.error('Error updating wheel:', err);
      setError('Failed to update wheel. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Back button */}
        <Link
          to={id ? `/wheels/${id}` : '/wheels'}
          className="inline-flex items-center text-primary hover:text-primary-dark mb-6"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Back to Wheel
        </Link>

        <h1 className="text-3xl font-bold text-dark mb-6">Edit Wheel</h1>

        {/* Error message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Wheel Name Input */}
        <div className="mb-6">
          <label htmlFor="wheelName" className="block text-gray-700 font-medium mb-2">
            Wheel Name
          </label>
          <input
            type="text"
            id="wheelName"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Enter a name for your wheel"
            value={wheelName}
            onChange={e => setWheelName(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Wheel Display */}
          <div className="flex justify-center flex-col items-center">
            <WheelDisplay
              entries={entries}
              size={400}
              colorScheme={entries.length > 0 ? undefined : 'default'}
              spinButtonText="Preview Spin"
            />
          </div>

          {/* Wheel Entries Section */}
          <div>
            <div className="card p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4">Wheel Entries</h2>
              <WheelEntryEditor entries={entries} onChange={setEntries} />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 mt-6">
              <button
                type="button"
                className={`px-6 py-2 ${
                  isSaving ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'
                } text-white rounded-lg transition-colors`}
                disabled={isSaving}
                onClick={handleSaveWheel}
              >
                {isSaving ? (
                  <>
                    <span className="inline-block animate-spin mr-2">↻</span>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
              <Link
                to={id ? `/wheels/${id}` : '/wheels'}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EditWheel;
